import React, { useState, useMemo, useEffect } from 'react';
import { X } from 'lucide-react';
import { getCoachMessage, isSnoozed, snoozeMessage } from '../../lib/coachMessages';
import Button from '../../design-system/Button';

/**
 * APEX v7.0 — Coach Header
 *
 * Header coach affiché en haut du Dashboard. Affiche le message le plus prioritaire
 * selon l'état de l'app (cf coachMessages.js).
 *
 * Props:
 *   - state         : { games, miniBlocks, rankSnapshots, importTriggers, userSettings }
 *   - onAction      : callback (actionType, payload?) => void
 *
 * Comportement :
 *   - Calcule le message coach via getCoachMessage(state)
 *   - Si le message est snoozé dans cette session, descend dans la hiérarchie
 *   - Sur snooze, retire le message courant pour cette session uniquement
 */

const TONE_STYLES = {
  critical: {
    accent: 'bg-rose-400',
    eyebrow: 'text-rose-400'
  },
  warning: {
    accent: 'bg-amber-400',
    eyebrow: 'text-amber-400'
  },
  positive: {
    accent: 'bg-gradient-to-b from-cyan-400 to-fuchsia-400',
    eyebrow: 'text-cyan-400'
  },
  neutral: {
    accent: 'bg-gradient-to-b from-cyan-400 to-fuchsia-400',
    eyebrow: 'text-cyan-400'
  }
};

export default function CoachHeader({ state, onAction }) {
  // Recalcule à chaque changement de state — mais on évite les recalculs inutiles
  const computeFn = () => getCoachMessage(state);
  const [snoozeVersion, setSnoozeVersion] = useState(0); // bump pour forcer recompute après snooze

  const message = useMemo(() => {
    // On évalue le message courant et on saute ceux qui sont snoozés.
    // Mais notre hiérarchie n'expose pas la chaîne directement.
    // Stratégie pragmatique : si le top message est snoozé, on force le default neutre.
    const m = computeFn();
    if (m && isSnoozed(m.id) && m.id !== 'default') {
      // On retombe sur un message neutre
      return getCoachMessage({ ...state, _forceDefault: true });
    }
    return m;
  }, [state, snoozeVersion]);

  if (!message) return null;

  const tone = TONE_STYLES[message.tone] || TONE_STYLES.neutral;
  const isCritical = message.tone === 'critical';

  const handleAction = (action, payload) => {
    onAction?.(action, payload);
  };

  const handleSnooze = (e) => {
    e.stopPropagation();
    snoozeMessage(message.id);
    setSnoozeVersion(v => v + 1);
  };

  // Pas de croix sur le default
  const canDismiss = message.id !== 'default';

  return (
    <div className="border-b border-zinc-800 bg-[#0f0f10]">
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 md:py-5">
        <div className="flex items-start gap-4">
          {/* Barre accent verticale */}
          <div className={`w-1 self-stretch ${tone.accent} shrink-0`} />

          {/* Contenu */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className={`text-[10px] tracking-[0.3em] uppercase font-semibold ${tone.eyebrow} mb-1`}>
                  {message.eyebrow}
                </div>
                <h2 className="text-lg md:text-xl font-bold text-white leading-tight">
                  {message.title}
                </h2>
                <p className="text-xs md:text-sm text-zinc-400 mt-2 leading-relaxed max-w-3xl">
                  {message.body}
                </p>
              </div>

              {/* Snooze button (croix discrète) */}
              {canDismiss && (
                <button
                  onClick={handleSnooze}
                  className="text-zinc-600 hover:text-zinc-400 transition shrink-0"
                  title="Ignorer pour cette session"
                  aria-label="Ignorer ce message"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* CTAs */}
            {(message.ctaPrimary || message.ctaSecondary) && (
              <div className="flex flex-wrap gap-2 mt-4">
                {message.ctaPrimary && (
                  <Button
                    variant={isCritical ? 'danger' : 'primary'}
                    size="sm"
                    onClick={() => {
                      if (message.ctaPrimary.action === 'snooze') {
                        handleSnooze({ stopPropagation: () => {} });
                      } else {
                        handleAction(message.ctaPrimary.action, message.ctaPrimary.payload);
                      }
                    }}
                  >
                    {message.ctaPrimary.label.toUpperCase()}
                  </Button>
                )}
                {message.ctaSecondary && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleAction(message.ctaSecondary.action, message.ctaSecondary.payload)}
                  >
                    {message.ctaSecondary.label.toUpperCase()}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
